import styled from '@emotion/styled';
import { Activity, useEffect, useState } from 'react';
import useAriaAnnouncer from '../../hooks/useAriaAnnouncer';
import { MEDIA_QUERY } from '../../styles/breakpoints';
import BaseLoading, { BaseLoadingProps } from './BaseLoading';

const Styled = {
  Wrapper: styled.main<PageLoadingProps>`
    position: relative;
    width: 100%;
    height: ${({ height }) => height || '70vh'};
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: ${MEDIA_QUERY.smMin}) {
      height: ${({ height }) => height || '80vh'};
    }
  `,
};

type PageLoadingProps = BaseLoadingProps & { height?: string; force?: boolean };

export default function PageLoading({
  size = 30,
  height,
  color = 'black',
  force = false,
}: PageLoadingProps) {
  const announce = useAriaAnnouncer();
  const [showSpinner, setShowSpinner] = useState(force);

  // Politely announce to screen readers that the page is loading. Announcing appends a node
  // to a log region, so every call is read out — keep it to once per mount, not once per render.
  useEffect(() => {
    announce('Loading');
  }, [announce]);

  // Page loads < 100 ms feel instantaneous, so only show spinner after that much time
  // https://www.pubnub.com/blog/how-fast-is-realtime-human-perception-and-technology/
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowSpinner(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <Styled.Wrapper height={height} data-testid="page-loading">
      <Activity mode={showSpinner ? 'visible' : 'hidden'}>
        <BaseLoading size={size} color={color} />
      </Activity>
    </Styled.Wrapper>
  );
}
